/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback } from "react";

// antd:
import { Switch, Form } from "antd";
// import type { RadioChangeEvent } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getAppState, getUser, changeAppState, onChangeTheme, onChangeCompact, onChangeLng, onChangeShowDesktopIconsTitle, getSafePtr } from "../../libs/reducer.ts"; // getAppState
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
// import type {TelementState} from "../../types/uiapp.d.ts";
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Title, Text, Link, Paragraph } = Typography;

const { rz } = window;

type Tswitch = {
  size:"default" | "small"
};

const RZswitch:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZswitch.displayName = "Switch";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");
  // const theme = useAppSelector(getTheme);

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  // const {iconOff, iconOn} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const switchType = useMemo<Tswitch>(() => {
    const typ:Tswitch = {size: "default"};
    (type || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["small"].indexOf(tt)) { typ.size = tt }
    });

    if (attributes?.size && ~["small", "default"].indexOf(attributes.size)) { typ.size = attributes.size }
    return typ;
  }, [type, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    return est;
  }, [elState.userStyle, elState?.visible, permissions]);

  const checkedChildren = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (elState.attributes?.iconOn) {
      ico = (<AppIcons key={key + "-icon-on"} icopath={elState.attributes?.iconOn} attrs={{className: elState.attributes?.iconOnClass || "rz-margin-right-5", color: elState.attributes?.iconOnColor}} />);
    }
    let ttl:React.ReactElement | undefined;
    if (elState.attributes["titleOn_" + lng] || elState.attributes.titleOn || elState.attributes["titleOn_" + rlng]) {
      ttl = (<SpanE key={key + "-title-on"} uk-tooltip={elState.attributes["tooltipOn_" + lng] || elState.attributes.tooltipOn || elState.attributes["tooltipOn_" + rlng]}>
        {elState.attributes["titleOn_" + lng] || elState.attributes.titleOn || elState.attributes["titleOn_" + rlng] || ""}
      </SpanE>);
    }
    return (<>{ico}{ttl}</>);
  }, [elState.attributes, key, lng, rlng]);

  const unCheckedChildren = useMemo(() => {
    let ico:React.ReactElement | undefined;
    if (elState.attributes?.iconOff) {
      ico = (<AppIcons key={key + "-icon-off"} icopath={elState.attributes?.iconOff} attrs={{className: elState.attributes?.iconOffClass || "rz-margin-right-5", color: elState.attributes?.iconOffColor}} />);
    }
    let ttl:React.ReactElement | undefined;
    if (elState.attributes["titleOff_" + lng] || elState.attributes.titleOff || elState.attributes["titleOff_" + rlng]) {
      ttl = (<SpanE key={key + "-title-off"} uk-tooltip={elState.attributes["tooltipOff_" + lng] || elState.attributes.tooltipOff || elState.attributes["tooltipOff_" + rlng]}>
        {elState.attributes["titleOff_" + lng] || elState.attributes.titleOff || elState.attributes["titleOff_" + rlng] || ""}
      </SpanE>);
    }
    return (<>{ico}{ttl}</>);
  }, [elState.attributes, key, lng, rlng]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line max-len, react-hooks/exhaustive-deps
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const onChange = useCallback((checked:boolean) => {
    if (fnc.config?.isDev) console.log({action: "<Switch key='" + key + "'> -> onChange()", value: checked});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value: checked}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: checked, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret?.globalState) {
            if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
            if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: checked, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret?.globalState) {
          if (ret.globalState.lng) { dispatch(onChangeLng(ret.globalState.lng, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.theme)) { dispatch(onChangeTheme(ret.globalState.theme, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.compact)) { dispatch(onChangeCompact(ret.globalState.compact, true)) }
          if (!rz.isUndefinedOrNull(ret.globalState.showDesktopIconsTitle)) { dispatch(onChangeShowDesktopIconsTitle(ret.globalState.showDesktopIconsTitle, true)) }
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  return (
    wrapperForm(
      <Switch key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
        checkedChildren={checkedChildren}
        unCheckedChildren={unCheckedChildren}
        // defaultChecked={elState?.value}
        checked={elState.value === true || elState.value === "true"}
        size={switchType.size}
        onChange={onChange}
        disabled={elState?.disabled || permissions.disabled === true}
      />
    )
  );
}

export default RZswitch;
/* eslint-enable @typescript-eslint/no-unused-vars */
